import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import _find from 'lodash/find';
import type { AnnotationLabelT } from '../../../../common/types/types';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { currentFrameAnnotationBitmapsAtom } from '../../components/annotations/atoms/segmentation-atoms';
import { eraseMaskFromOtherAnnotations, filterSegmentationAnnotations } from '../../components/annotations/canvas/utils';
import { ANNOTATION_TYPE, SAM_ANNOTATION_ACTIONS } from '../../constants';
import type { AnnotationT, BrushAnnotationT } from '../../types/annotations.type';
import type { Sam3AutoDetectBuildPendingItemArgsT, Sam3CommittedPendingItemT } from './use-sam3-auto-detect';
import { useSam3AutoDetect } from './use-sam3-auto-detect';
import { useSegmentationBitmapUpdater } from './use-segmentation-bitmap-updater';
import useStage from './use-stage';
type PendingMask = {
  mask: string;
};
const isSegmentationAnnotation = (annotation: AnnotationT | undefined): annotation is BrushAnnotationT => annotation?.annotationType === ANNOTATION_TYPE.SEGMENTATION;
export const useSam3ForAutoDetectMask = () => {
  const {
    currentFrameAtom,
    annotationStoreAtom,
    annotationsUnderReviewBitmapsAtom
  } = useStageAtoms();
  const currentFrame = useAtomValue(currentFrameAtom);
  const annotationStore = useAtomValue(annotationStoreAtom);
  const reviewBitmaps = useAtomValue(annotationsUnderReviewBitmapsAtom);
  const annotationBitmap = useAtomValue(currentFrameAnnotationBitmapsAtom);
  const {
    addAnnotation,
    updateAnnotation
  } = useStage();
  const {
    updateBitmapOnAccept,
    removeReviewBitmapOnReject
  } = useSegmentationBitmapUpdater();
  const buildPendingItem = useCallback(async ({
    result,
    selectedAnnotationLabel,
    textPrompt
  }: Sam3AutoDetectBuildPendingItemArgsT) => {
    if (!result.mask) return undefined;
    const strokeColor = selectedAnnotationLabel.style?.strokeColor ?? '#ffffff';
    return {
      pending: {
        mask: result.mask
      },
      reviewEntry: {
        type: SAM_ANNOTATION_ACTIONS.AUTO_DETECT,
        annotationMetaData: [{
          annotationLabel: selectedAnnotationLabel,
          textPrompt
        }],
        annotationData: [{
          base64String: result.mask,
          color: strokeColor
        }]
      }
    };
  }, []);
  const upsertSegmentationAnnotation = useCallback((selectedAnnotationLabel: AnnotationLabelT, pending: PendingMask[], updatedBase64String?: string | null) => {
    const allAnnotationIDs = Object.keys(annotationStore ?? {});
    const existingSegmentationID = _find(allAnnotationIDs, key => {
      const annotation = annotationStore[key];
      return isSegmentationAnnotation(annotation) && annotation.annotationLabel.title === selectedAnnotationLabel.title;
    });
    if (existingSegmentationID !== undefined) {
      const existingAnnotation = annotationStore[existingSegmentationID];
      if (isSegmentationAnnotation(existingAnnotation)) {
        updateAnnotation(existingSegmentationID, {
          ...existingAnnotation,
          data: {
            ...existingAnnotation.data,
            points: [...(existingAnnotation.data.points ?? [])],
            base64String: updatedBase64String ?? ''
          }
        });
      }
      return existingSegmentationID;
    }
    const firstMask = pending[0]?.mask ?? '';
    return addAnnotation({
      annotationType: ANNOTATION_TYPE.SEGMENTATION,
      annotationLabel: selectedAnnotationLabel,
      data: {
        base64String: updatedBase64String ?? firstMask,
        color: selectedAnnotationLabel.style?.strokeColor ?? '#ffffff',
        points: []
      }
    });
  }, [annotationStore, addAnnotation, updateAnnotation]);
  const onSubmitAll = useCallback((pending: Sam3CommittedPendingItemT<PendingMask>[]) => {
    const [firstPending] = pending;
    if (!firstPending) return;
    const selectedAnnotationLabel = firstPending.annotationLabel;
    const maskData = pending.map(({
      data
    }) => data);

    // Commit once — the review bitmap is already the full composite of all N detected masks.
    const updatedBase64String = updateBitmapOnAccept(selectedAnnotationLabel, currentFrame, reviewBitmaps);
    const acceptedAnnotationId = upsertSegmentationAnnotation(selectedAnnotationLabel, maskData, updatedBase64String);
    const newMaskBitmap = reviewBitmaps[selectedAnnotationLabel.title];
    if (newMaskBitmap) {
      eraseMaskFromOtherAnnotations(newMaskBitmap, selectedAnnotationLabel.style?.strokeColor ?? '#ffffff', filterSegmentationAnnotations(annotationStore), annotationBitmap, acceptedAnnotationId, updateAnnotation);
    }
  }, [currentFrame, annotationStore, reviewBitmaps, annotationBitmap, updateBitmapOnAccept, updateAnnotation, upsertSegmentationAnnotation]);
  const onCancelAll = useCallback((pending: Sam3CommittedPendingItemT<PendingMask>[]) => {
    const selectedAnnotationLabel = pending[0]?.annotationLabel;
    if (selectedAnnotationLabel) {
      removeReviewBitmapOnReject(selectedAnnotationLabel.title);
    }
  }, [removeReviewBitmapOnReject]);
  return useSam3AutoDetect<PendingMask>({
    annotationType: 'MASK',
    buildPendingItem,
    onSubmitAll,
    onCancelAll
  });
};